import { io } from 'socket.io-client';
import { reactive } from 'vue';
import {detectAndExtractTools} from "@/utils/utils";

class AgentSocketClient {
    constructor() {
        this.state = reactive({
            socket: null,
            isConnected: false,
            currentAgent: null,
            sessionId: null,
            messages: [],
            isTyping: false,
            error: null,
            trainingStatus: null,
            userId: null,

            // Builder states
            builderAgent: null,
            builderSessionId: null,
            builderMessages: [],
            isBuilderTyping: false,
            builderError: null,
            isBuilderConnected: false
        });

        this.baseDomain = import.meta.env.VITE_AI_API_URL;
    }

    connect(userId) {
        if (!this.state.socket) {
            this.state.userId = userId;
            this.state.socket = io(this.baseDomain, {
                withCredentials: true,
                transports: ['websocket']
            });

            this.state.socket.on('connect', () => {
                console.log('Connected to agent socket server');
                this.state.isConnected = true;
                this.state.socket.emit('join_agent_creator', userId);
            });

            this.state.socket.on('disconnect', () => {
                console.log('Disconnected from agent socket server');
                this.state.isConnected = false;
                this.state.currentAgent = null;
                this.state.builderAgent = null;
                this.state.isBuilderConnected = false;
            });

            this.state.socket.on('agent_training_complete', (data) => {
                console.log('Agent training completed:', data);
                this.state.trainingStatus = { success: true, data: data.agent };
            });

            this.state.socket.on('agent_training_failed', (data) => {
                console.log('Agent training failed:', data);
                this.state.trainingStatus = { success: false, error: data.error };
            });

            this.state.socket.on('agent_joined', (data) => {
                console.log('Joined agent:', data);
                this.state.currentAgent = data;
                this.state.sessionId = data.sessionId;
                this.state.error = null;
            });

            this.state.socket.on('agent_response', (data) => {
                console.log('Agent response:', data);
                this.state.messages.push({
                    id: Date.now(),
                    role: 'assistant',
                    message: data.message,
                    timestamp: new Date(data.timestamp)
                });
                this.state.isTyping = false;
            });

            this.state.socket.on('agent_typing', (data) => {
                this.state.isTyping = data.typing;
            });

            this.state.socket.on('agent_message_history', (data) => {
                console.log('Message history:', data);
                this.state.messages = data.messages.map(msg => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));
            });

            this.state.socket.on('agent_left', () => {
                console.log('Left agent');
                this.state.currentAgent = null;
                this.state.sessionId = null;
                this.state.messages = [];
            });

            this.state.socket.on('agent_error', (data) => {
                console.error('Agent error:', data);
                this.state.error = data.message;
                this.state.isTyping = false;
                this.state.isBuilderTyping = false;
            });

            this.state.socket.on('agent_builder_joined', (data) => {
                console.log('Joined agent builder:', data);
                this.state.builderAgent = data;
                this.state.builderSessionId = data.sessionId;
                this.state.builderError = null;
                this.state.isBuilderConnected = true;
            });

            this.state.socket.on('agent_builder_response', (data) => {
                console.log('Agent builder response:', data);

                const { cleanedMessage, detectedTools } = detectAndExtractTools(data.message);

                this.state.builderMessages.push({
                    id: Date.now(),
                    role: 'assistant',
                    message: cleanedMessage,
                    run: detectedTools.length > 0 ? detectedTools : null,
                    timestamp: new Date(data.timestamp)
                });

                this.state.isBuilderTyping = false;
            });

            this.state.socket.on('agent_builder_typing', (data) => {
                this.state.isBuilderTyping = data.typing;
            });

            this.state.socket.on('agent_builder_message_history', (data) => {
                console.log('Builder message history:', data);
                this.state.builderMessages = data.messages.map(msg => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));
            });

            this.state.socket.on('agent_builder_left', () => {
                console.log('Left agent builder');
                this.state.builderAgent = null;
                this.state.builderSessionId = null;
                this.state.builderMessages = [];
                this.state.isBuilderConnected = false;
            });

            this.state.socket.on('agent_builder_notification', (data) => {
                console.log('Builder notification:', data);
            });
        }
    }

    async joinAgent(agentToken) {
        if (!this.state.socket || !this.state.isConnected) {
            throw new Error('Socket not connected');
        }

        this.state.error = null;
        this.state.socket.emit('join_agent', {
            agentToken,
            userId: this.state.userId
        });
    }

    async sendMessage(message) {
        if (!this.state.socket || !this.state.currentAgent) {
            throw new Error('Not connected to any agent');
        }

        // Add user message to local state immediately
        const userMessage = {
            id: Date.now(),
            role: 'user',
            message: message,
            timestamp: new Date()
        };
        this.state.messages.push(userMessage);

        // Send to server
        this.state.socket.emit('send_agent_message', {
            message,
            sessionId: this.state.sessionId,
            userId: this.state.userId
        });
    }

    leaveAgent() {
        if (this.state.socket && this.state.currentAgent) {
            this.state.socket.emit('leave_agent');
        }
    }

    getMessageHistory() {
        if (this.state.socket && this.state.currentAgent) {
            this.state.socket.emit('get_agent_history', {
                sessionId: this.state.sessionId,
                userId: this.state.userId
            });
        }
    }

    // NEW BUILDER METHODS
    async joinAgentBuilder(agentToken, sessionId = null) {
        if (!this.state.socket || !this.state.isConnected) {
            throw new Error('Socket not connected');
        }

        this.state.builderError = null;
        this.state.socket.emit('join_agent_builder', {
            agentToken,
            sessionId,
            userId: this.state.userId
        });
    }

    async sendBuilderMessage(message) {
        if (!this.state.socket || !this.state.builderAgent) {
            throw new Error('Not connected to any agent builder');
        }

        // Add user message to local state immediately
        const userMessage = {
            id: Date.now(),
            role: 'user',
            message: message,
            timestamp: new Date()
        };
        this.state.builderMessages.push(userMessage);

        // Send to server
        this.state.socket.emit('send_agent_builder_message', {
            message,
            sessionId: this.state.builderSessionId,
            userId: this.state.userId
        });
    }

    leaveAgentBuilder() {
        if (this.state.socket && this.state.builderAgent) {
            this.state.socket.emit('leave_agent_builder');
        }
    }

    getBuilderMessageHistory() {
        if (this.state.socket && this.state.builderAgent) {
            this.state.socket.emit('get_agent_builder_history', {
                sessionId: this.state.builderSessionId,
                userId: this.state.userId
            });
        }
    }

    clearBuilderMessages() {
        this.state.builderMessages = [];
    }

    clearTrainingStatus() {
        this.state.trainingStatus = null;
    }

    disconnect() {
        if (this.state.socket) {
            this.leaveAgent();
            this.leaveAgentBuilder();
            if (this.state.userId) {
                this.state.socket.emit('leave_agent_creator', this.state.userId);
            }
            this.state.socket.disconnect();
            this.state.socket = null;
            this.state.isConnected = false;
            this.state.currentAgent = null;
            this.state.messages = [];
            this.state.builderAgent = null;
            this.state.builderMessages = [];
            this.state.isBuilderConnected = false;
        }
    }

    isConnectedToAgent() {
        return this.state.currentAgent !== null;
    }

    isConnectedToBuilder() {
        return this.state.builderAgent !== null;
    }

    getCurrentAgentName() {
        return this.state.currentAgent?.agentName?.en || this.state.currentAgent?.agentName || '';
    }

    getBuilderAgentName() {
        return this.state.builderAgent?.agentName?.en || this.state.builderAgent?.agentName || '';
    }
}

export default new AgentSocketClient();
